import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Box from '@mui/material/Box'
import { IsThereAvatarBanner } from '@/components/IsThereAvatarBanner'
import {
  EmtBreadcrumbs,
  EmtButton,
  EmtCard,
  EmtGridContainer,
  EmtHeader,
  EmtIcon,
  EmtTypography,
  useLargeTablet,
  useMobile,
} from 'emt-components-v2'
import { Images, Routes } from '@/assets/configs'
import { Constants } from '@/assets'
import NextLink from 'next/link'
import Skeleton from '@mui/material/Skeleton'
import {
  Availabilities,
  DogAbout,
  DogProperties,
  MobileHeader,
  ProfileContainer,
  ProfileMap,
  ProfileSidebarComponent,
  UserDetails,
  UserImagesList,
  UserImagesListLoading,
  Wrapper,
} from '@/components/Profile/partials'
import Container from '@mui/material/Container'
import Comments from '@/components/Profile/containers/Comments'
import { getReviews } from '@/modules/Reviews/ReviewsServiceHelper'
import Grid from '@mui/material/Grid'
import { ImagesSliderModal } from '@/components/Profile/Modals'
import Footer from '@/components/EMT/Footer'
import { useIsQueryOwner } from '@/hooks'
import { useNotifications } from '@/modules/Notifications'
import { useQuery } from '@tanstack/react-query'
import { useRouter } from 'next/router'
import Have from '@/components/Profile/partials/Have'
import { messageSanitize } from '@/helpers/messageSanitize'
import ProfileCardV2 from '@/components/EMT/ProfileCardV2'
import { useFavoritesV2 } from '@/modules/Favorites'
import { searchProfilesAsync } from '@/modules/Search/SearchServiceHelper'
import { SearchType } from '@/modules/Search/apiTypes/base'
import { getAnimalProfile, getMemberProfile } from '@/modules/Profile/ProfileServiceHelper'
import useCurrentUser from '@/hooks/useCurrentUser'
import ProfileSidebarStickyCard, {
  ProfileSideBarUserCard,
} from '@/components/Profile/partials/ProfileSidebarStickyCard'
import PremiumModal from '@/components/Premium/PremiumModal'
import { useTranslation } from 'next-i18next'
import { usePetsittingMode } from '@/store/usePetsittingMode'

type Props = React.PropsWithChildren<{
  customerId: string
  overrideBackButton?: () => void
  getUserProfileName?: (name?: string) => void
}>

const REVIEWS_PER_PAGE = 10

type ReviewsSummaryState = {
  reviews: any[]
  averageRating: number
  reviewsCount: number
}

const INITIAL_REVIEWS_SUMMARY: ReviewsSummaryState = {
  reviews: [],
  averageRating: 0,
  reviewsCount: 0,
}

const ProfileComponent: React.FC<Props> = ({ customerId, overrideBackButton, getUserProfileName }) => {
  const { t } = useTranslation('profile')
  const { t: tc } = useTranslation('common')
  const [isQueryOwner, type, appendQueryType] = useIsQueryOwner()
  const [isMobile, isTablet] = useMobile()
  const isLargeTablet = useLargeTablet()
  const { isPetsittingMode } = usePetsittingMode()

  const [activeStickyProfile, setActiveStickyProfile] = useState<boolean>(false)
  const [activeModalSlider, setActiveModalSlider] = useState<boolean>(false)
  const [imageClickedIndex, setImageClickedIndex] = useState<number>(0)
  const [profileUserSidebar, setUserProfileSidebar] = useState<number | undefined>(undefined)
  const [showMoreDescription, setShowMoreDescription] = useState<boolean>(false)
  const [activePremiumModal, setActivePremiumModal] = useState<boolean>(false)
  const [addNotificationError] = useNotifications()
  const { isPremium, settings, currentUser } = useCurrentUser()
  const router = useRouter()

  const backOrRedirectHome = () => {
    if (typeof window !== 'undefined' && window.history.length > 1) {
      router.back()
      return
    }

    router.push(appendQueryType(Routes.Home))
  }

  // 🔥Get user profile
  const { isLoading, refetch, ...profileFetching } = useQuery(
    [customerId],
    async () => {
      const fetchCustomerProfile = () => (isQueryOwner ? getMemberProfile(customerId) : getAnimalProfile(customerId))
      try {
        const profile = await fetchCustomerProfile()
        if (!profile) {
          if (Constants.node_env === 'production') {
            backOrRedirectHome()
          } else {
            addNotificationError({ error: new Error('Profile not found') })
            overrideBackButton?.()
          }
          return
        }

        if (getUserProfileName) {
          getUserProfileName(profile.displayName)
        }
        return profile
      } catch (error) {
        if (Constants.node_env === 'production') {
          backOrRedirectHome()
        } else {
          addNotificationError({ error })
          overrideBackButton?.()
        }
      }
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 10000,
    },
  )
  const profile = profileFetching.data

  // Reviews summary state
  const [reviewsSummary, setReviewsSummary] = useState<ReviewsSummaryState>(INITIAL_REVIEWS_SUMMARY)
  const [reviewsPage, setReviewsPage] = useState(1)
  const [hasMoreReviews, setHasMoreReviews] = useState(false)
  const [reviewsLoadingMore, setReviewsLoadingMore] = useState(false)

  const reviewTargetId = isQueryOwner ? profile?.id : (profile as any)?.owner?.id

  const fetchReviews = useCallback(
    async (page: number, append = false) => {
      if (!reviewTargetId) return

      const reviewsData = await getReviews(reviewTargetId, {
        page,
        perPage: REVIEWS_PER_PAGE,
        sortBy: 'date',
        direction: 'desc',
      })

      const mappedReviews = (reviewsData.lastReviews || []).map((r: any) => ({
        ...r,
        comment: r.content,
        reviewDate: r.createdAt,
        displayName: r.author,
        rating: r.rating,
        thumbnailUrl: r.authorAvatarUrl || '',
      }))

      let nextReviewsCount = 0
      setReviewsSummary((prev) => {
        const nextReviews = append ? [...prev.reviews, ...mappedReviews] : mappedReviews
        nextReviewsCount = nextReviews.length

        return {
          reviews: nextReviews,
          averageRating: reviewsData.averageRating,
          reviewsCount: reviewsData.totalReviewsCount,
        }
      })

      setHasMoreReviews(nextReviewsCount < reviewsData.totalReviewsCount)
      setReviewsPage(page)
    },
    [reviewTargetId],
  )

  const refreshReviews = useCallback(async () => {
    await fetchReviews(1)
  }, [fetchReviews])

  const loadMoreReviews = useCallback(async () => {
    if (!hasMoreReviews || reviewsLoadingMore) return

    setReviewsLoadingMore(true)
    try {
      await fetchReviews(reviewsPage + 1, true)
    } catch (e) {
      // ignore reviews error
    } finally {
      setReviewsLoadingMore(false)
    }
  }, [fetchReviews, hasMoreReviews, reviewsLoadingMore, reviewsPage])

  useEffect(() => {
    if (!reviewTargetId) {
      setReviewsSummary(INITIAL_REVIEWS_SUMMARY)
      setReviewsPage(1)
      setHasMoreReviews(false)
      return
    }

    refreshReviews().catch(() => {
      // ignore reviews error
    })
  }, [refreshReviews, reviewTargetId])

  const { id: profileId, ['@favorite']: favoriteIRI, description = '', displayName = '' } = profile || {}

  const profileLastRefresh = useMemo(() => {
    if (!profile) return undefined

    const source = profile as { lastRefresh?: unknown }

    return typeof source?.lastRefresh === 'string' ? source.lastRefresh : undefined
  }, [profile])

  const userCardToContact = useMemo(() => {
    let card: ProfileSideBarUserCard | undefined
    if (profile?.['@type'] === 'AnimalProfile' && profile.owner) {
      // Get the owner Card + the reviewIRI from the Animal Profile
      card = {
        ...profile.owner,
        '@review': profile['@review'],
      }
    }

    if (!card && profile) {
      // Convert Profile to ProfileSideBarUserCard
      card = {
        id: profile.id,
        displayName: profile.displayName,
        distance: profile.distance,
        thumbnail: profile.mainPictureUrl,
        isPremium: profile.isPremium,
        isCertified: profile.isCertified,
        isFavorite: profile.isFavorite,
        '@review': profile['@review'],
      }
    }

    return card
  }, [profile])

  // Favorite
  const [generateFavorite, isFavoriteLoading] = useFavoritesV2(profileId || customerId, favoriteIRI)

  // 🔥Get matched profiles
  const { isLoading: isMatchedProfilesLoading, data: matchingCards } = useQuery(
    [`${currentUser?.id || 'noid'}_matched_profiles`],
    async () => {
      const fetchedData = await searchProfilesAsync(isQueryOwner, {
        type: SearchType.recommendation,
        userAddress: settings?.address,
      })

      return fetchedData.member
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 10000,
    },
  )

  useIsQueryOwner()
  const handleImageClick = (imageIndex: number) => {
    setImageClickedIndex(imageIndex)
    setActiveModalSlider(true)
  }

  const getDescription = (): { description: string; showMore: boolean } => {
    if (!isMobile) return { description, showMore: false }
    if (description.length > 499 && !showMoreDescription) {
      return { description: `${description.slice(0, 499)}...`, showMore: true }
    } else if (description.length > 499 && showMoreDescription) {
      return { description, showMore: true }
    }
    return { description, showMore: false }
  }

  // ? Profil sidebar event
  useEffect(() => {
    if (isLoading || isMobile || !profileId) return
    const profileSidebarEl = document.querySelector('#profile-sidebar')?.getBoundingClientRect()
    setUserProfileSidebar(profileSidebarEl?.top)
  }, [isLoading])

  useEffect(() => {
    if (!profileUserSidebar) return

    if (userCardToContact?.animals?.length) {
      setActiveStickyProfile(true)
    } else {
      const isSticky = () => {
        const scrollTop = window.scrollY
        setActiveStickyProfile(scrollTop >= profileUserSidebar - 10)
      }

      window.addEventListener('scroll', isSticky)
      return () => {
        window.removeEventListener('scroll', isSticky)
      }
    }
  }, [profileUserSidebar, userCardToContact?.animals?.length])

  const onFavoriteButtonClick = useCallback(async () => {
    if (userCardToContact) {
      await generateFavorite(userCardToContact.isFavorite)
      await refetch()
    }
  }, [userCardToContact?.isFavorite, generateFavorite, refetch])

  const onContactButtonClick = useCallback(() => {
    if (userCardToContact) {
      if (!isPremium) {
        setActivePremiumModal(true)
      } else {
        router.push(appendQueryType(Routes.Message._pathname, { profile: userCardToContact.id }))
      }
    }
  }, [isPremium, appendQueryType, userCardToContact?.id])

  return (
    <>
      <>
        {/* DESKTOP Header */}
        {!isMobile && (
          <EmtHeader background={Images.Header.Pattern} backgroundColor={isPetsittingMode ? '#762D8A' : '#E01B3F'}>
            <IsThereAvatarBanner t={tc} showHeader={false} sx={{ paddingBottom: '30px' }} />
            {!isLoading && profileId ? (
              <EmtBreadcrumbs variant="white">
                <NextLink href={appendQueryType(Routes.Home)} passHref legacyBehavior>
                  <Box sx={{ display: 'flex', gap: '5px', cursor: 'pointer' }}>
                    <EmtIcon className="emt-paw" />
                    {tc('breadcrumbs.home')}
                  </Box>
                </NextLink>
                <NextLink href={appendQueryType(Routes.Search._pathname)} passHref legacyBehavior>
                  {tc(`navigation.search.${type}`)}
                </NextLink>
                <EmtTypography>{displayName}</EmtTypography>
              </EmtBreadcrumbs>
            ) : (
              <Skeleton variant="text" height={24} width="40%" />
            )}
          </EmtHeader>
        )}
        {/* END DESKTOP Header */}
        {/* MOBILE Header */}
        {!isLoading && isMobile && profile ? (
          <>
            <IsThereAvatarBanner t={tc} sx={{ paddingTop: '80px' }} />
            <MobileHeader
              picturesUrls={profile.picturesUrls}
              handleImageClick={handleImageClick}
              overrideBackButton={overrideBackButton}
              userIsNew={profile.isNew}
              userLastRefresh={profileLastRefresh}
              reviewsCount={reviewsSummary?.reviewsCount}
              rating={reviewsSummary?.averageRating}
              distance={profile.distance}
            />
          </>
        ) : isMobile ? (
          <Skeleton variant="rectangular" height={320} width="100%" />
        ) : null}
        {/* END MOBILE Header */}
        {/* CONTENT */}
        <Wrapper>
          <Container
            sx={{
              paddingTop: '30px',
              display: 'flex',
              gap: '20px',
            }}
            maxWidth="lg"
          >
            {/* Profil Container */}
            <ProfileContainer>
              {!isLoading && !!profile ? (
                <>
                  {/* DESKTOP Images List */}
                  {!isMobile && (
                    <UserImagesList
                      picturesUrls={profile.picturesUrls}
                      userIsNew={profile.isNew}
                      userLastRefresh={profileLastRefresh}
                      onClick={(imageIndex) => handleImageClick(imageIndex)}
                      reviewsCount={reviewsSummary?.reviewsCount}
                      rating={reviewsSummary?.averageRating}
                      distance={profile.distance}
                    />
                  )}
                  {/* END DESKTOP Images list */}
                  {/* User Details */}
                  <UserDetails
                    profile={{
                      ...profile,
                      reviewsSummary: {
                        averageRating: reviewsSummary.averageRating,
                        totalReviews: reviewsSummary.reviewsCount,
                      },
                    }}
                  />
                  {/* END User Details */}

                  {/* Dog properties */}
                  {profile['@type'] === 'AnimalProfile' && <DogProperties profile={profile} />}
                  {/* END Dog properties */}

                  {/* Description */}
                  <Box>
                    <EmtTypography
                      variant="body1"
                      sx={{
                        display: 'inline',
                        whiteSpace: 'pre-wrap',
                        textOverflow: 'ellipsis',
                        maxWidth: '100%',
                        overflow: 'hidden',
                      }}
                    >
                      {messageSanitize(getDescription().description, { address: false, keywords: false })}
                    </EmtTypography>
                    {getDescription().showMore && !showMoreDescription ? (
                      <EmtTypography
                        variant="body1"
                        fontWeight="bold"
                        color="primary"
                        sx={{ cursor: 'pointer', display: 'inline', marginLeft: '8px' }}
                        onClick={() => setShowMoreDescription(true)}
                      >
                        {t('about.showMore')}
                      </EmtTypography>
                    ) : null}
                    {getDescription().showMore && showMoreDescription ? (
                      <EmtTypography
                        variant="body1"
                        fontWeight="bold"
                        color="primary"
                        sx={{ cursor: 'pointer', display: 'inline', marginLeft: '8px' }}
                        onClick={() => setShowMoreDescription(false)}
                      >
                        {t('about.showLess')}
                      </EmtTypography>
                    ) : null}
                  </Box>
                  {/* END Description */}
                  {/* Position */}
                  <ProfileMap address={{ latitude: profile.latitude, longitude: profile.longitude }} />
                  {/* END Position */}

                  {/* Dog about */}
                  {profile['@type'] === 'AnimalProfile' ? <DogAbout profile={profile} /> : null}
                  {/* END Dog about */}

                  {/* Availabilities */}
                  <Availabilities profile={profile} />

                  {isQueryOwner ? <Have profile={profile} /> : null}
                  {/* END Availabilities */}

                  {!!userCardToContact && isMobile && !!userCardToContact.animals?.length && (
                    <ProfileSidebarStickyCard
                      profile={userCardToContact}
                      onContactButtonClick={onContactButtonClick}
                      isFavoriteLoading={isFavoriteLoading}
                      onFavoriteButtonClick={onFavoriteButtonClick}
                    />
                  )}

                  {/* Comments */}
                  {!!userCardToContact && (
                    <Box id="reviews">
                      <Comments
                        // @ts-expect-error
                        profile={{ ...userCardToContact, reviewsSummary }}
                        hasMoreReviews={hasMoreReviews}
                        reviewsLoadingMore={reviewsLoadingMore}
                        onLoadMoreReviews={loadMoreReviews}
                        // showGiveFeedback
                        // onReplySuccess={fetchReviews}
                      />
                    </Box>
                  )}
                  {/* END Comments */}
                </>
              ) : (
                <>
                  <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: '30px' }}>
                    {!isMobile && <UserImagesListLoading />}
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Skeleton variant="text" width="100%" height={58} />
                      <Skeleton variant="rectangular" width="100%" height={12} />
                    </Box>
                    <Box sx={{ display: 'flex', gap: '25px' }}>
                      <Skeleton variant="rectangular" height={120} width="100%" sx={{ borderRadius: '18px' }} />
                      <Skeleton variant="rectangular" height={120} width="100%" sx={{ borderRadius: '18px' }} />
                      <Skeleton variant="rectangular" height={120} width="100%" sx={{ borderRadius: '18px' }} />
                    </Box>
                    <Box>
                      <Skeleton variant="text" width="100%" height={20} />
                      <Skeleton variant="text" width="90%" height={20} />
                      <Skeleton variant="text" width="85%" height={20} />
                    </Box>
                    <Box>
                      <Skeleton variant="rectangular" width="100%" height={260} />
                    </Box>
                  </Box>
                </>
              )}
            </ProfileContainer>
            {/* END Profil Container */}
            {/* Profil Sidebar */}
            {!!userCardToContact && (
              <ProfileSidebarComponent
                profile={userCardToContact}
                isLoaded={!isLoading}
                activeStickyProfile={activeStickyProfile}
                onContactButtonClick={onContactButtonClick}
                onFavoriteButtonClick={onFavoriteButtonClick}
                isFavoriteLoading={isFavoriteLoading}
              />
            )}
            {/* END Profil Sidebar */}
          </Container>
        </Wrapper>
        {/* END CONTENT */}
        {/* SIMILAR CARDS */}
        <Container
          sx={{
            paddingTop: isMobile ? 0 : '24px',
            display: 'flex',
            flexDirection: 'column',
            rowSpacing: '30px',
            gap: '20px',
            paddingLeft: isMobile ? '6px' : '0',
            paddingRight: isMobile ? '6px' : '0',
          }}
          maxWidth="lg"
        >
          <EmtTypography variant="h4" fontWeight={800}>
            {t(`proximity.title.${type}`)}
          </EmtTypography>

          <EmtGridContainer sx={{ paddingLeft: isMobile ? '10px' : '0' }}>
            {isMatchedProfilesLoading
              ? [...Array(isMobile || isTablet || isLargeTablet ? 4 : 6)].map((v, k) => (
                  <Grid key={k} item xs={6} md={3} lg={2}>
                    <EmtCard loader />
                  </Grid>
                ))
              : matchingCards?.slice(0, isMobile || isTablet || isLargeTablet ? 4 : 6).map((matchingCard, k) => (
                  <Grid key={k} item xs={6} md={3} lg={2} sx={{ paddingLeft: isMobile ? '15px !important' : '0' }}>
                    <ProfileCardV2 profile={matchingCard} sx={{ minWidth: !isMobile && !isTablet ? 185 : '100%' }} />
                  </Grid>
                ))}
          </EmtGridContainer>
          <Box sx={{ textAlign: 'center' }}>
            <NextLink href={appendQueryType(Routes.Search._pathname)} passHref legacyBehavior>
              <EmtButton variant="outlined" color="primary">
                {t(`proximity.button.${type}`)}
              </EmtButton>
            </NextLink>
          </Box>
        </Container>
        {/* END SIMILAR CARDS */}
        {/* MODAL Carousel */}
        {!isLoading && activeModalSlider && (
          <ImagesSliderModal
            picturesUrls={profile?.picturesUrls || []}
            initialSlide={imageClickedIndex}
            open={activeModalSlider}
            onClose={() => setActiveModalSlider(false)}
          />
        )}
        {/* END MODAL Carousel */}
      </>
      <Footer />

      {/* No premium modal */}
      <PremiumModal
        isFixed
        hideNavigation
        activeSecondaryContent
        open={activePremiumModal}
        onClose={() => setActivePremiumModal(false)}
        zIndex={isMobile ? 999999 : 998}
      />
      {/* END No premium modal */}
    </>
  )
}
export default ProfileComponent
